import { timeAgo } from "@/format/time";
import { useFlagSuperOnaEnabled } from "@/hooks/use-flag";
import type { PlainAgentExecution } from "@/queries/agent-queries";
import type { PlainEnvironment } from "@/queries/environment-queries";
import { parseTodoActivity } from "@/routes/environments/parseTodoActivity";
import { getEnvironmentLabel, isAutonomousWait, summarizeWaitingInfo } from "@/routes/environments/conversation-status";
import { agentExecutionState, AgentExecutionState } from "@/utils/agent-executions";
import { Timestamp } from "@bufbuild/protobuf";
import { useMemo, type FC } from "react";

// ── Ref extraction (backend-provided data only) ─────────────────────────

type RefInfo = { label: string; url?: string };

type MetadataSegment = {
    text: string;
    url?: string;
};

function getOutputString(agentExecution: PlainAgentExecution, key: string): string | undefined {
    const output = agentExecution.status?.outputs?.[key];
    if (output?.value?.case === "stringValue") {
        return output.value.value;
    }
    return undefined;
}

function extractPrNumberFromUrl(url: string): string | undefined {
    const match = /\/(?:pull|merge_requests)\/(\d+)/.exec(url);
    return match?.[1];
}

/**
 * Collects PR references from agent execution outputs only.
 * No client-side inference from conversation names.
 */
function resolvePrs(agentExecution: PlainAgentExecution): RefInfo[] {
    const seen = new Set<string>();
    const prs: RefInfo[] = [];

    const outputs = agentExecution.status?.outputs;
    if (!outputs) {
        return prs;
    }

    for (const key of Object.keys(outputs)) {
        if (key === "pr_url" || /^pr_url_\d+$/.test(key)) {
            const url = getOutputString(agentExecution, key);
            if (url) {
                const number = extractPrNumberFromUrl(url);
                if (number && !seen.has(number)) {
                    seen.add(number);
                    prs.push({ label: `PR #${number}`, url });
                }
            }
        }
    }

    return prs;
}

/**
 * Collects ticket references from agent execution outputs and annotations only.
 * No client-side inference from conversation names.
 */
function resolveTickets(agentExecution: PlainAgentExecution): RefInfo[] {
    const seen = new Set<string>();
    const tickets: RefInfo[] = [];

    const add = (id: string, url?: string) => {
        if (!seen.has(id)) {
            seen.add(id);
            tickets.push({ label: id, url });
        }
    };

    // Linear annotation
    const linearId = agentExecution.metadata?.annotations?.["linear.app/issue-identifier"];
    if (linearId) {
        add(linearId);
    }

    // Agent outputs: ticket_id, ticket_id_2, ticket_id_3, ...
    const outputs = agentExecution.status?.outputs;
    if (outputs) {
        for (const key of Object.keys(outputs)) {
            if (key === "ticket_id" || /^ticket_id_\d+$/.test(key)) {
                const id = getOutputString(agentExecution, key);
                if (id) {
                    const urlKey = key.replace("ticket_id", "ticket_url");
                    add(id, getOutputString(agentExecution, urlKey));
                }
            }
        }
    }

    return tickets;
}

/**
 * Formats a list of references into a single metadata segment.
 * Shows the first item, then "+N more" if there are additional ones.
 */
function formatRefs(refs: RefInfo[]): MetadataSegment | undefined {
    if (refs.length === 0) {
        return undefined;
    }
    if (refs.length === 1) {
        return { text: refs[0].label, url: refs[0].url };
    }
    return { text: `${refs[0].label} +${refs.length - 1} more`, url: refs[0].url };
}

// ── State helpers ────────────────────────────────────────────────────────

function isActiveState(state: AgentExecutionState | null): boolean {
    return state === AgentExecutionState.WORKING || state === AgentExecutionState.PENDING;
}

/**
 * Returns the state label for non-active states, or undefined to skip.
 * For WAITING_FOR_INPUT, prefers a descriptive summary from waitingInfo.
 */
function getStateLabel(state: AgentExecutionState | null, agentExecution?: PlainAgentExecution): string | undefined {
    switch (state) {
        case AgentExecutionState.FAILED:
            return "Failed";
        case AgentExecutionState.STOPPING:
            return "Stopping";
        case AgentExecutionState.WAITING_FOR_INPUT:
            return (agentExecution && summarizeWaitingInfo(agentExecution)) ?? "Waiting for input";
        case AgentExecutionState.READY_FOR_REVIEW:
            return "Ready for review";
        default:
            return undefined;
    }
}

/**
 * Resolves the activity text for active states.
 * "Ready to review" is a stale value left by the todo tool after all
 * items complete — ignore it while the agent is still running.
 */
function resolveActivity(currentActivity: string | undefined, state: AgentExecutionState): string {
    if (currentActivity && currentActivity !== "Ready to review") {
        return currentActivity;
    }
    switch (state) {
        case AgentExecutionState.PENDING:
            return "Starting…";
        default:
            return "Working…";
    }
}

// ── Metadata builder ────────────────────────────────────────────────────

function buildConversationMetadata(
    agentExecution: PlainAgentExecution,
    state: AgentExecutionState | null,
    branchName?: string,
): MetadataSegment[] {
    const segments: MetadataSegment[] = [];

    // Branch name (from backend via environment git status)
    if (branchName) {
        segments.push({ text: branchName });
    }

    // PR references (from agent execution outputs only)
    const prSegment = formatRefs(resolvePrs(agentExecution));
    if (prSegment) {
        segments.push(prSegment);
    }

    // Ticket references (from annotations and outputs only)
    const ticketSegment = formatRefs(resolveTickets(agentExecution));
    if (ticketSegment) {
        segments.push(ticketSegment);
    }

    // State label
    const stateLabel = getStateLabel(state, agentExecution);
    if (stateLabel) {
        segments.push({ text: stateLabel });
    }

    // Relative timestamp — "Now" if under 2 minutes
    const updatedAt = agentExecution.metadata?.updatedAt;
    if (updatedAt) {
        const date = new Timestamp(updatedAt).toDate();
        const diffSeconds = Math.abs(Date.now() - date.getTime()) / 1000;
        segments.push({ text: diffSeconds < 120 ? "Now" : timeAgo(date) });
    }

    return segments;
}

// ── Component ───────────────────────────────────────────────────────────

const METADATA_CLASS = "block truncate text-sm text-content-muted";

const ActivityText: FC<{ activity: string }> = ({ activity }) => {
    const parsed = parseTodoActivity(activity);

    if (parsed.isTodoActivity) {
        return (
            <span className="inline-flex items-center gap-1 truncate">
                <span className="shrink-0 rounded bg-surface-muted px-1 text-xs text-content-muted">
                    {parsed.current}/{parsed.total}
                </span>
                <span className="truncate">{parsed.description}</span>
            </span>
        );
    }

    return <>{activity}</>;
};

/**
 * Legacy metadata rendering — used when the superona flag is off.
 * Shows the raw activity text or execution state label.
 */
const LegacyMetadata: FC<{ agentExecution?: PlainAgentExecution }> = ({ agentExecution }) => {
    if (!agentExecution) {
        return null;
    }

    if (agentExecution.status?.currentActivity) {
        const parsed = parseTodoActivity(agentExecution.status.currentActivity);
        if (parsed.isTodoActivity) {
            return (
                <span className="flex items-center gap-1">
                    <span className="shrink-0 rounded bg-surface-muted px-1 text-xs text-content-muted">
                        {parsed.current}/{parsed.total}
                    </span>
                    <span className="block truncate font-mono text-sm text-content-muted">{parsed.description}</span>
                </span>
            );
        }
        return <span className="block truncate font-mono text-sm text-content-muted">{parsed.description}</span>;
    }

    const state = agentExecutionState(agentExecution);
    if (!state) {
        return null;
    }
    return <span className="block truncate font-mono text-sm text-content-muted">{state}</span>;
};

export const ConversationMetadata: FC<{
    env: PlainEnvironment;
    agentExecution?: PlainAgentExecution;
    branchName?: string;
}> = ({ env, agentExecution, branchName }) => {
    const { value: isSuperOnaEnabled } = useFlagSuperOnaEnabled();

    if (isSuperOnaEnabled) {
        return <EnhancedMetadata env={env} agentExecution={agentExecution} branchName={branchName} />;
    }

    return <LegacyMetadata agentExecution={agentExecution} />;
};

const EnhancedMetadata: FC<{
    env: PlainEnvironment;
    agentExecution?: PlainAgentExecution;
    branchName?: string;
}> = ({ env, agentExecution, branchName }) => {
    const state = agentExecution ? agentExecutionState(agentExecution) : null;
    const autonomousWait =
        state === AgentExecutionState.WAITING_FOR_INPUT && agentExecution && isAutonomousWait(agentExecution);
    const active = isActiveState(state) || autonomousWait;

    // Active: show only what the agent is doing — PR/ticket/time are noise
    const activity = useMemo(() => {
        if (!active || !state || !agentExecution) {
            return undefined;
        }
        // Autonomous wait: show a compact summary of what the agent is waiting on
        if (autonomousWait) {
            return summarizeWaitingInfo(agentExecution) ?? "Working…";
        }
        return resolveActivity(agentExecution?.status?.currentActivity, state);
    }, [active, state, agentExecution, autonomousWait]);

    // Non-active: show branch, PR refs, ticket refs, state label, timestamp
    const segments = useMemo(() => {
        if (active) {
            return undefined;
        }
        if (!agentExecution) {
            return [{ text: getEnvironmentLabel(env) }] as MetadataSegment[];
        }
        return buildConversationMetadata(agentExecution, state, branchName);
    }, [active, agentExecution, env, state, branchName]);

    if (activity) {
        return (
            <span className={METADATA_CLASS}>
                <ActivityText activity={activity} />
            </span>
        );
    }

    if (!segments || segments.length === 0) {
        return null;
    }

    return (
        <span className={METADATA_CLASS}>
            {segments.map((segment, i) => (
                <span key={i}>
                    {i > 0 && <span className="mx-1">&middot;</span>}
                    {segment.url ? (
                        <a
                            href={segment.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hover:underline"
                            data-tracking-id="conversation-metadata-ref-link"
                            onClick={(e) => e.stopPropagation()}
                            onContextMenu={(e) => e.stopPropagation()}
                        >
                            {segment.text}
                        </a>
                    ) : (
                        <span>{segment.text}</span>
                    )}
                </span>
            ))}
        </span>
    );
};
